import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "./Services";
import { SERVICES } from "../../lib/constants";
import { api, errMsg } from "../../lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";

export function Pricing() {
  return (
    <div>
      <PageHero overline="Pricing" title="Flat rates. Zero variables."
        sub="Every quote is a locked flat rate based on scope — never an hourly meter. Here's where pricing starts." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SERVICES.map((s) => (
            <div key={s.id} data-testid={`pricing-card-${s.id}`} className="rounded-xl border bg-card p-7 card-lift flex flex-col">
              <s.icon className="w-5 h-5 text-[var(--cc-accent)]" />
              <h3 className="font-heading font-semibold text-lg mt-4">{s.name}</h3>
              <p className="font-heading font-extrabold text-3xl mt-3">{s.from}<span className="text-sm font-normal text-muted-foreground">{s.from !== "Custom" ? " from" : ""}</span></p>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed flex-1">{s.blurb}</p>
              <Link to="/quote" state={{ service: s.id }} className="mt-5">
                <Button variant="outline" size="sm" className="rounded-full w-full">Get exact quote</Button>
              </Link>
            </div>
          ))}
        </div>
        <div className="mt-14 rounded-xl border bg-secondary/40 p-8">
          <h3 className="font-heading font-semibold text-lg mb-5">Every flat rate includes</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {["Vetted, uniformed crew", "Truck, fuel & equipment", "Blankets, wrap & protection", "Insurance up to $1M", "Live mission tracking", "Assembly & disassembly", "No fuel surcharges", "No stair or long-carry fees"].map((x) => (
              <p key={x} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[var(--cc-accent)] shrink-0" /> {x}</p>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const FAQS = [
  ["How does flat-rate pricing work?", "We scope your job — inventory, access, distance, timing — and lock a single price. That price doesn't change unless the scope changes. No hourly meters, no fuel surcharges, no stair fees."],
  ["How fast can you deploy a crew?", "Same-day is often available in San Diego and Oceanside. Long-distance corridors to LA and Vegas typically book 3–7 days out."],
  ["Are your crews employees or day labor?", "Employees. Every operative is background-checked, trained on our protocols, and performance-tracked. We never staff jobs from labor apps."],
  ["What does the $1M insurance cover?", "Full liability coverage on every mission. High-value items receive documented condition reports before and after transit."],
  ["Can I track my job?", "Yes — your customer portal shows live mission status, assigned crew, timeline events, documents, and invoices. It's the same picture our operations center sees."],
  ["How do payments work?", "Invoices are issued through your portal and payable online by card via secure Stripe checkout. Commercial accounts can arrange net terms."],
  ["Do you handle long-distance moves?", "Yes — dedicated corridors between San Diego County, Los Angeles, and Las Vegas with locked flat rates and scheduled delivery windows."],
  ["What if something is damaged?", "Report it through your portal or crew lead. Claims are acknowledged within 24 hours and resolved fast — that's what the coverage is for."],
];

export function FAQ() {
  return (
    <div>
      <PageHero overline="FAQ" title="Answers, up front." sub="If it's not covered here, the AI assistant in the corner knows the rest." />
      <section className="max-w-3xl mx-auto px-5 py-20">
        <Accordion type="single" collapsible className="space-y-3">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`q${i}`} className="rounded-xl border bg-card px-6">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-heading font-semibold text-left hover:no-underline">{q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}

const ROLES = [];

export function Careers() {
  const [applyRole, setApplyRole] = useState(null);
  return (
    <div>
      <PageHero overline="Careers" title="Join the operation."
        sub="We hire for discipline and train for skill. Real wages, real advancement, real standards." />
      <section className="max-w-3xl mx-auto px-5 py-20">
        <div className="rounded-xl border bg-card p-10 text-center card-lift">
          <p className="overline-cc">General Application</p>
          <h3 className="font-heading font-bold text-2xl mt-3">Send us your résumé.</h3>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-lg mx-auto">
            We're always scouting operatives, drivers, and coordinators. Tell us who you are and what you've done — we'll reach out when the right role opens.
          </p>
          <Button data-testid="career-apply-general" onClick={() => setApplyRole("General Application")}
            className="mt-7 rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold px-8">
            Apply Now <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
      <ApplyDialog role={applyRole} onClose={() => setApplyRole(null)} />
    </div>
  );
}

function ApplyDialog({ role, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", experience: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/public/apply", { ...form, role });
      toast.success("Application received — we'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", experience: "", message: "" });
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setLoading(false);
  };

  return (
    <Dialog open={!!role} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="career-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Apply — {role}</DialogTitle>
          <DialogDescription>Takes under a minute. We reply to every application.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="app-name">Full name</Label>
            <Input id="app-name" data-testid="career-form-name" value={form.name} onChange={set("name")} placeholder="Jordan Smith" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="app-email">Email</Label>
              <Input id="app-email" data-testid="career-form-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="app-phone">Phone</Label>
              <Input id="app-phone" data-testid="career-form-phone" value={form.phone} onChange={set("phone")} placeholder="(619) 555-0100" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="app-exp">Relevant experience</Label>
            <Input id="app-exp" data-testid="career-form-experience" value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years on a moving crew" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="app-msg">Why CastleCorp?</Label>
            <Textarea id="app-msg" data-testid="career-form-message" rows={3} value={form.message} onChange={set("message")} placeholder="Tell us about yourself..." />
          </div>
          <Button type="submit" data-testid="career-form-submit" disabled={loading}
            className="w-full rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Application"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const Legal = ({ title, sections }) => (
  <div>
    <PageHero overline="Legal" title={title} />
    <section className="max-w-3xl mx-auto px-5 py-20 space-y-10">
      {sections.map(([h, body]) => (
        <div key={h}>
          <h2 className="font-heading font-semibold text-xl mb-3">{h}</h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
        </div>
      ))}
      <p className="text-xs text-muted-foreground font-mono-cc">Last updated: June 2026 · Questions: contact@castle-corp.com</p>
    </section>
  </div>
);

export function Privacy() {
  return <Legal title="Privacy Policy" sections={[
    ["Information We Collect", "We collect information you provide when requesting quotes, creating an account, booking services, or contacting us: name, email, phone, service addresses, and job details. Payment card data is processed by Stripe and never stored on our servers."],
    ["How We Use It", "To deliver and coordinate services, issue quotes and invoices, communicate about your jobs, improve our operations, and comply with legal obligations. We do not sell your personal information."],
    ["Data Security", "Data is encrypted in transit, access-controlled internally, and stored with industry-standard safeguards. Authentication uses secure, httpOnly session cookies."],
    ["Your Rights", "You may request access to, correction of, or deletion of your personal data at any time by emailing contact@castle-corp.com. California residents have additional rights under the CCPA."],
    ["Cookies", "We use essential cookies for authentication and session management only. We do not run third-party advertising trackers."],
  ]} />;
}

export function Terms() {
  return <Legal title="Terms of Service" sections={[
    ["Services", "CastleCorp provides moving, delivery, furniture assembly, junk removal, and labor services under flat-rate quotes. A quote becomes binding when you confirm a booking, and covers the scope described in it."],
    ["Quotes & Payment", "Quotes are valid for 14 days. Invoices are due per the terms stated on them and payable online via Stripe. Late invoices may pause future scheduling."],
    ["Scope Changes", "If job scope materially changes (additional inventory, access issues not disclosed, added stops), we will present a revised flat rate before proceeding."],
    ["Liability & Claims", "Missions are insured up to $1,000,000. Claims must be reported within 5 business days. We repair, replace, or compensate per coverage terms."],
    ["Cancellations", "Cancel or reschedule at no charge up to 48 hours before your window. Inside 48 hours, a reservation fee may apply."],
    ["Acceptable Use", "You agree not to misuse the platform, attempt unauthorized access, or ship prohibited/hazardous items without disclosure."],
  ]} />;
}
